"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check"
import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy"
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle"
import { Button } from "@/components/ui/button"
import { copyText } from "@/lib/clipboard"
import { cn } from "@/lib/utils"

/** Keep this control beside links/buttons, never nested inside another button. */
export function CopyButton({ value, label = "mã xe", className }: {
  value?: string | null
  label?: string
  className?: string
}) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle")
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    setStatus("idle")
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [value])
  if (!value) return null
  const message = status === "copied" ? `Đã sao chép ${label}` : status === "failed" ? "Không sao chép được. Bạn có thể chọn văn bản để sao chép." : ""
  return <span className="relative inline-flex shrink-0 items-center">
    <Button
      type="button" variant="ghost" size="icon"
      data-copy-trigger
      aria-label={`Sao chép ${label}`}
      title={message || `Sao chép ${label}: ${value}`}
      className={cn("size-6 p-1 text-muted-foreground hover:text-primary [@media(pointer:coarse)]:size-8", status === "copied" && "text-success", status === "failed" && "text-destructive", className)}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={async (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (timer.current) clearTimeout(timer.current)
        try { await copyText(value); setStatus("copied") }
        catch { setStatus("failed") }
        timer.current = setTimeout(() => setStatus("idle"), 2000)
      }}
    >
      {status === "copied" ? <CheckIcon className="size-3.5" aria-hidden="true" /> : status === "failed" ? <WarningCircleIcon className="size-3.5" aria-hidden="true" /> : <CopyIcon className="size-3.5" aria-hidden="true" />}
    </Button>
    <span role="status" className="sr-only">{message}</span>
  </span>
}

/** Display formatting may differ, but copying always uses the complete source value. */
export function CopyableText({ value, label = "mã xe", children, className, textClassName }: {
  value?: string | null
  label?: string
  children?: ReactNode
  className?: string
  textClassName?: string
}) {
  if (!value) return <span className={className}>{children ?? "—"}</span>
  return <span className={cn("relative inline-flex min-w-0 max-w-full items-center gap-1 align-middle", className)}>
    <span className={cn("min-w-0 break-all", textClassName)}>{children ?? value}</span>
    <CopyButton value={value} label={label} />
  </span>
}
